/**
 * Device Registration Service for Capacitor
 * Handles registering mobile devices with the backend for push notifications
 */

import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { isNativePlatform, getPlatform } from './platform';

export interface DeviceInfo {
  deviceId: string;
  platform: 'ios' | 'android';
  model?: string;
  osVersion?: string;
  appVersion?: string;
  deviceName?: string;
}

export interface DeviceRegistrationData extends DeviceInfo {
  pushToken?: string;
  voipToken?: string;
  pushEnabled: boolean;
}

let cachedDeviceInfo: DeviceInfo | null = null;

/**
 * Get device information
 */
export async function getDeviceInfo(): Promise<DeviceInfo | null> {
  if (!isNativePlatform()) {
    return null;
  }

  if (cachedDeviceInfo) {
    return cachedDeviceInfo;
  }

  try {
    const [deviceInfo, appInfo, deviceId] = await Promise.all([
      Device.getInfo(),
      App.getInfo(),
      Device.getId(),
    ]);

    cachedDeviceInfo = {
      deviceId: deviceId.identifier,
      platform: getPlatform() as 'ios' | 'android',
      model: deviceInfo.model,
      osVersion: deviceInfo.osVersion,
      appVersion: appInfo.version,
      deviceName: deviceInfo.name,
    };

    return cachedDeviceInfo;
  } catch (error) {
    console.error('Error getting device info:', error);
    return null;
  }
}

/**
 * Register device with backend
 */
const VOIP_READY_KEY = 'ic-voip-push-ready';

/** True only after the backend confirmed it will VoIP-push this phone for incoming calls. */
export function canRingNatively(): boolean {
  try { return localStorage.getItem(VOIP_READY_KEY) === '1'; } catch { return false; }
}

export async function registerDevice(
  apiClient: { post: (url: string, data: any, config?: any) => Promise<any> },
  authToken: string,
  pushToken?: string,
  voipToken?: string,
): Promise<{ success: boolean; deviceId?: string; error?: string; voipPushReady?: boolean }> {
  if (!isNativePlatform()) {
    return { success: false, error: 'Not a native platform' };
  }

  try {
    const deviceInfo = await getDeviceInfo();
    if (!deviceInfo) {
      return { success: false, error: 'Could not get device info' };
    }

    const registrationData: DeviceRegistrationData = {
      ...deviceInfo,
      pushToken,
      voipToken,
      pushEnabled: !!pushToken,
    };

    const response = await apiClient.post('/api/mobile/devices/register', registrationData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'X-App-Platform': deviceInfo.platform,
        'X-App-Version': deviceInfo.appVersion || '',
        'X-Device-Id': deviceInfo.deviceId,
        'X-OS-Version': deviceInfo.osVersion || '',
        'X-Client-Info': 'insuracentral-mobile',
      },
    });

    // The backend says whether it will actually VoIP-push THIS phone for
    // incoming calls (token saved AND APNs configured). Persisted so the shell
    // can decide things like "is call forwarding redundant here?" — a phone
    // that can't be rung natively must keep forwarding.
    const voipPushReady = !!response.data?.data?.voipPushReady;
    try { localStorage.setItem(VOIP_READY_KEY, voipPushReady ? '1' : '0'); } catch { /* ignore */ }
    return {
      success: true,
      deviceId: response.data?.data?.id || deviceInfo.deviceId,
      voipPushReady,
    };
  } catch (error: any) {
    console.error('Error registering device:', error);
    return {
      success: false,
      error: error.message || 'Failed to register device',
    };
  }
}

/**
 * Update device push tokens
 */
export async function updateDeviceTokens(
  apiClient: { put: (url: string, data: any, config?: any) => Promise<any> },
  authToken: string,
  pushToken?: string,
  voipToken?: string,
): Promise<{ success: boolean; error?: string }> {
  if (!isNativePlatform()) {
    return { success: false, error: 'Not a native platform' };
  }

  try {
    const deviceInfo = await getDeviceInfo();
    if (!deviceInfo) {
      return { success: false, error: 'Could not get device info' };
    }

    await apiClient.put(
      `/api/mobile/devices/${deviceInfo.deviceId}/tokens`,
      { pushToken, voipToken },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'X-App-Platform': deviceInfo.platform,
          'X-Device-Id': deviceInfo.deviceId,
          'X-Client-Info': 'insuracentral-mobile',
        },
      },
    );

    return { success: true };
  } catch (error: any) {
    console.error('Error updating device tokens:', error);
    return {
      success: false,
      error: error.message || 'Failed to update tokens',
    };
  }
}

/**
 * Send device heartbeat (app opened/active)
 */
export async function sendHeartbeat(
  apiClient: { post: (url: string, data: any, config?: any) => Promise<any> },
  authToken: string,
): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    const deviceInfo = await getDeviceInfo();
    if (!deviceInfo) {
      return;
    }

    await apiClient.post(
      `/api/mobile/devices/${deviceInfo.deviceId}/heartbeat`,
      {},
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'X-App-Platform': deviceInfo.platform,
          'X-Device-Id': deviceInfo.deviceId,
          'X-Client-Info': 'insuracentral-mobile',
        },
      },
    );
  } catch (error) {
    // Heartbeat failures are not critical
    console.warn('Error sending heartbeat:', error);
  }
}

/**
 * Update push enabled status
 */
export async function updatePushEnabled(
  apiClient: { put: (url: string, data: any, config?: any) => Promise<any> },
  authToken: string,
  enabled: boolean,
): Promise<{ success: boolean; error?: string }> {
  if (!isNativePlatform()) {
    return { success: false, error: 'Not a native platform' };
  }

  try {
    const deviceInfo = await getDeviceInfo();
    if (!deviceInfo) {
      return { success: false, error: 'Could not get device info' };
    }

    await apiClient.put(
      `/api/mobile/devices/${deviceInfo.deviceId}/push-enabled`,
      { enabled },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'X-App-Platform': deviceInfo.platform,
          'X-Device-Id': deviceInfo.deviceId,
          'X-Client-Info': 'insuracentral-mobile',
        },
      },
    );

    return { success: true };
  } catch (error: any) {
    console.error('Error updating push enabled:', error);
    return {
      success: false,
      error: error.message || 'Failed to update push status',
    };
  }
}

/**
 * Deactivate device (on logout)
 */
export async function deactivateDevice(
  apiClient: { delete: (url: string, config?: any) => Promise<any> },
  authToken: string,
): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    const deviceInfo = await getDeviceInfo();
    if (!deviceInfo) {
      return;
    }

    await apiClient.delete(`/api/mobile/devices/${deviceInfo.deviceId}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'X-App-Platform': deviceInfo.platform,
        'X-Device-Id': deviceInfo.deviceId,
        'X-Client-Info': 'insuracentral-mobile',
      },
    });
  } catch (error) {
    console.error('Error deactivating device:', error);
  }
}

/**
 * Get request headers for mobile app
 */
export async function getMobileHeaders(): Promise<Record<string, string>> {
  const deviceInfo = await getDeviceInfo();
  if (!deviceInfo) {
    return {};
  }

  return {
    'X-App-Platform': deviceInfo.platform,
    'X-App-Version': deviceInfo.appVersion || '',
    'X-Device-Id': deviceInfo.deviceId,
    'X-OS-Version': deviceInfo.osVersion || '',
    'X-Client-Info': 'insuracentral-mobile',
  };
}
